/**
 * Locations Section Mapper
 * Maps Content API data directly to LocationsProps
 */
import {centralAmericaLocations, usaMapLocations} from "@/_fake-data/locations-data";

import type {LocationsProps} from "@/sections/content/locations/locations";

import {transformPadding} from "@/entities/flexible/api/transform-padding";

import {assetPath} from "@/shared/utils/asset-path";

import type {ContentApiLocationsSection} from "./type";

/**
 * Static map data for locations
 * Maps location id to svgMap path and mapData
 */
const STATIC_LOCATION_DATA = {
    "north-america": {
        svgMap: assetPath("/images/locations/usa-map.svg"),
        mapData: usaMapLocations,
    },
    "central-america": {
        svgMap: assetPath("/images/locations/central-america.svg"),
        mapData: centralAmericaLocations,
    },
} as const;

/**
 * Default static locations (not stored in Sanity)
 */
const DEFAULT_LOCATION_IDS = ["north-america", "central-america"] as const;

/**
 * Map Content API Locations section directly to LocationsProps
 */
export function mapLocationsSection(section: ContentApiLocationsSection, t: (key: string) => string): LocationsProps {
    // Locations are always static (not from Sanity)
    // Map static locations to Locations component format with translations
    const locations = DEFAULT_LOCATION_IDS.map((locationId) => {
        const staticData = STATIC_LOCATION_DATA[locationId];
        const translatedTitle = t(locationId);

        return {
            id: locationId,
            title: translatedTitle,
            svgMap: staticData?.svgMap,
            mapData: staticData?.mapData,
        };
    });

    return {
        title: section.title || "",
        description: section.subtitle,
        locations,
        space: transformPadding(section.padding),
        anchorId: section.anchor_id,
    };
}
